const MAX_FILE_BYTES = 30 * 1024 * 1024;
const RASTERIZE_MAX_EDGE = 4096;

function loadImageElement(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Could not read the image."));
    img.src = src;
  });
}

function isSvgFile(file: File): boolean {
  return file.type === "image/svg+xml" || /\.svg$/i.test(file.name);
}

function canvasToBlob(canvas: HTMLCanvasElement, type: string, quality?: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error("Could not process the image."));
          return;
        }
        resolve(blob);
      },
      type,
      quality,
    );
  });
}

/**
 * Returns a normalized `blob:` URL for THREE.TextureLoader.
 * All image types (PNG/JPEG/WebP/SVG) are rasterized and downscaled so huge
 * source files don't explode GPU memory on upload.
 */
export async function createLogoDisplayUrl(file: File): Promise<string> {
  if (file.size > MAX_FILE_BYTES) {
    throw new Error("Maximum file size is 30 MB.");
  }

  const okType =
    file.type.startsWith("image/") ||
    /\.(png|jpe?g|webp|svg)$/i.test(file.name);
  if (!okType) {
    throw new Error("Please choose an image file (PNG, JPEG, WebP, or SVG).");
  }

  const blobUrl = URL.createObjectURL(file);
  try {
    const img = await loadImageElement(blobUrl);
    let cw = img.naturalWidth || img.width;
    let ch = img.naturalHeight || img.height;
    if (cw < 1 || ch < 1) {
      cw = 512;
      ch = 512;
    }
    // Never upscale tiny images; only constrain oversized uploads.
    const scale = Math.min(1, RASTERIZE_MAX_EDGE / cw, RASTERIZE_MAX_EDGE / ch);
    const rw = Math.max(1, Math.round(cw * scale));
    const rh = Math.max(1, Math.round(ch * scale));
    const canvas = document.createElement("canvas");
    canvas.width = rw;
    canvas.height = rh;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Could not process the SVG file.");
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(img, 0, 0, rw, rh);
    const outType = isSvgFile(file) ? "image/png" : "image/webp";
    const outBlob = await canvasToBlob(canvas, outType, 0.92);
    return URL.createObjectURL(outBlob);
  } finally {
    URL.revokeObjectURL(blobUrl);
  }
}
